/**
 * Resident Row Component
 *
 * Displays a single resident row in the Residents table.
 * 15 columns: frozen patient info + risk + notes + days + 6 insights + 5 risk factors.
 */

import { useQuery } from "@tanstack/react-query";
import { ResidentSummary } from "../../types/residents";
import { useApiService } from "../../contexts/ApiServiceContext";
import { CursorTooltip } from "../CursorTooltip";
import { PatientInfoCell } from "../molecules/PatientInfoCell";
import { RiskLevelBadge } from "../molecules/RiskLevelBadge";
import { STYLES } from "../../config/styles";
import {
  IndicatorBadge,
  IndicatorColumnConfig,
  NEW_INSIGHT_COLUMNS,
  PRIMARY_RISK_COLUMNS,
  getInsightValue,
  getRiskFactorValue,
} from "../molecules/FactorIndicatorGroup";
import { EllipsisVertical, FileText, CircleCheck, EyeOff } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";

export interface ResidentRowClickData {
  resident: ResidentSummary;
}

interface ResidentRowProps {
  resident: ResidentSummary;
  index: number;
  isLastRow: boolean;
  onClick: (data: ResidentRowClickData) => void;
  onAddNote: (resident: ResidentSummary) => void;
  onReviewCompleted: (resident: ResidentSummary) => void;
  onHideResident: (resident: ResidentSummary) => void;
  insightsLookback?: number;
  visibleInsightColumns?: IndicatorColumnConfig[];
  visibleRiskColumns?: IndicatorColumnConfig[];
  isAdmittedVisible?: boolean;
  style?: React.CSSProperties;
}

export function ResidentRow({
  resident,
  index,
  isLastRow,
  onClick,
  onAddNote,
  onReviewCompleted,
  onHideResident,
  insightsLookback = 7,
  visibleInsightColumns = NEW_INSIGHT_COLUMNS,
  visibleRiskColumns = PRIMARY_RISK_COLUMNS,
  isAdmittedVisible = true,
  style,
}: ResidentRowProps) {
  const api = useApiService();
  const { data: trendData } = useQuery({
    queryKey: ["risk-trend", resident.resident_id],
    queryFn: () => api.getResidentRiskTrend(resident.wq_id ?? resident.resident_id),
    staleTime: 5 * 60 * 1000,
    enabled: resident.risk_level !== "UNKNOWN",
  });

  // Use period-specific trend from API; fall back to overall trend for older data
  const trend =
    trendData?.trend_by_period?.[String(insightsLookback)] ?? trendData?.trend;

  const isEvenRow = index % 2 === 0;
  const rowBg = isEvenRow ? "bg-white" : "bg-[#F9FAFB]";
  const cellBorderB = isLastRow ? "" : "border-b border-border";

  return (
    <CursorTooltip
      key={`${resident.resident_id}-${index}`}
      content="View Details"
      onClick={() => onClick({ resident })}
      className={`cursor-pointer transition-colors hover:bg-muted/50 ${rowBg}`}
      style={style}
    >
      {/* 0. Action Menu */}
      <td className={`p-1 text-center ${cellBorderB}`} onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 rounded hover:bg-muted">
              <EllipsisVertical className="h-4 w-4 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[180px]">
            <DropdownMenuItem onSelect={() => onAddNote(resident)}>
              <FileText className="h-4 w-4" />
              Add Note
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onReviewCompleted(resident)}>
              <CircleCheck className="h-4 w-4 text-success" />
              Review Completed
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => onHideResident(resident)}>
              <EyeOff className="h-4 w-4" />
              Hide for # days
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>

      {/* 1. Patient Information (frozen) */}
      <PatientInfoCell
        residentName={resident.resident_name}
        room={resident.room}
        dateOfBirth={resident.date_of_birth}
        lastMdsDate={resident.data_sources_used?.mds_completed_date}
        mdsType={resident.data_sources_used?.mds_isc_code}
        onClick={() => onClick({ resident })}
        className={`sticky left-0 ${rowBg} border-r border-border ${cellBorderB}`}
      />

      {/* 2. Risk Level + Notes + Days (merged cell) */}
      <td colSpan={2} className={`${STYLES.table.cellPadding} ${cellBorderB} border-l border-border`}>
        <div className="flex flex-col items-center gap-1">
          <RiskLevelBadge level={resident.risk_level} trend={trend} />
          <div className="flex w-full">
            <span className="flex-1 text-center text-sm font-bold">
              {resident.cq_notes_total} <span className="font-medium text-muted-foreground text-xs">notes</span>
            </span>
            <span className={`flex-1 text-center text-sm font-bold ${resident.days_since_last_review === null ? "text-muted-foreground" : ""}`}>
              {resident.days_since_last_review !== null
                ? resident.days_since_last_review
                : "—"} <span className="font-medium text-muted-foreground text-xs">days</span>
            </span>
          </div>
        </div>
      </td>

      {/* Admitted < 30 Days (standalone column) */}
      {isAdmittedVisible && (
        <td className={`p-1 text-center ${cellBorderB} border-l border-border`}>
          <div className="flex justify-center">
            <IndicatorBadge
              value={getRiskFactorValue(resident.primary_risk_factors, "admitted_lt_30_days")}
            />
          </div>
        </td>
      )}

      {/* 5-10. New Insight indicator cells */}
      {visibleInsightColumns.map((col, i) => (
        <td key={col.key} className={`p-1 text-center ${cellBorderB} ${i === 0 ? "border-l border-border" : ""}`}>
          <div className="flex justify-center">
            <IndicatorBadge
              value={getInsightValue(resident.new_insights, col.key)}
            />
          </div>
        </td>
      ))}

      {/* 11-15. Primary Risk Factor indicator cells */}
      {visibleRiskColumns.map((col, i) => (
        <td key={col.key} className={`p-1 text-center ${cellBorderB} ${i === 0 ? "border-l border-border" : ""}`}>
          <div className="flex justify-center">
            <IndicatorBadge
              value={getRiskFactorValue(resident.primary_risk_factors, col.key)}
            />
          </div>
        </td>
      ))}
    </CursorTooltip>
  );
}
